import React, { useState, useEffect, useMemo, useRef } from "react";

// ══════════════════════════════════════════════════════════════════════════════
// SolarLead Territory Picker — v2
//
// Anchored dropdown from the TERRITORY button. Two modes: browse + build.
// Hierarchy: All Markets → US/Canada → States/Provinces.
// Every row carries MoM delta — picker is also an intelligence surface.
//
// BACKEND TODOs (component runs on fallback + localStorage until backend ships):
//   - GET  /api/territories/list           → { usStates: [...], caProvinces: [...] }
//                                            (legacy fallback: { states: [...] })
//   - GET  /api/cities/search?q=...        → { results: [{name, state, permits}] }
//   - GET  /api/cities/nearby?cities=...   → { results: [{name, permits}] }
//   - POST /api/territories/saved          → persist saved custom territory
//
// Persistence today:
//   - sl_recent_territories  → last 3 selected (JSON array)
//   - sl_saved_territories   → user's saved custom bundles (JSON array)
// ══════════════════════════════════════════════════════════════════════════════

type Territory = {
  name: string;
  type: 'all' | 'state' | 'province' | 'custom';
  country?: 'US' | 'CA';
  count: number;       // active permits
  momPct: number;      // MoM growth %
  cities?: number;     // for custom territories
  cityList?: string[]; // for custom territories
};

type CityResult = { name: string; state: string; permits: number };

type AnchorPos = { top: number; left: number };

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (name: string) => void;
  currentTerritory: string;
  apiBase?: string;
  anchorPos?: AnchorPos;
};

// ── Design tokens ──
const T = {
  surface: "#FFFFFF",
  surfaceRaised: "#FAFAF7",
  surfaceMagic: "#FFF7ED",
  text: "#0A0A0A",
  textSub: "#5A5A6E",
  primary: "#FF8800",
  primaryDark: "#E07700",
  border: "#E5E5E0",
  borderSoft: "#F0F0EB",
  borderHairline: "#EBEBE5",
  borderDark: "#D8D8D0",
  grow: "#15803D",
  defend: "#EF4444",
};

const FONT_DISPLAY = "'Outfit', system-ui, -apple-system, sans-serif";
const FONT_MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Consolas, monospace";

// ── "All Markets" canonical option ──
const ALL_MARKETS: Territory = {
  name: 'All Markets',
  type: 'all',
  count: 900409,
  momPct: 8,
};

// ── Fallback data — used until backend wires up ──
const FALLBACK_US_STATES: Territory[] = [
  { name: 'California', type: 'state', country: 'US', count: 142890, momPct: 3 },
  { name: 'Florida', type: 'state', country: 'US', count: 18205, momPct: 14 },
  { name: 'Texas', type: 'state', country: 'US', count: 12402, momPct: 18 },
  { name: 'New Jersey', type: 'state', country: 'US', count: 8932, momPct: 6 },
  { name: 'North Carolina', type: 'state', country: 'US', count: 7540, momPct: 12 },
  { name: 'New York', type: 'state', country: 'US', count: 6108, momPct: 4 },
  { name: 'Massachusetts', type: 'state', country: 'US', count: 5420, momPct: 2 },
  { name: 'Nevada', type: 'state', country: 'US', count: 4202, momPct: 16 },
  { name: 'Maryland', type: 'state', country: 'US', count: 3402, momPct: 5 },
  { name: 'Arizona', type: 'state', country: 'US', count: 3108, momPct: 22 },
  { name: 'Colorado', type: 'state', country: 'US', count: 2910, momPct: 9 },
  { name: 'Connecticut', type: 'state', country: 'US', count: 2330, momPct: 1 },
  { name: 'Utah', type: 'state', country: 'US', count: 2108, momPct: 11 },
  { name: 'Hawaii', type: 'state', country: 'US', count: 1890, momPct: -2 },
  { name: 'Pennsylvania', type: 'state', country: 'US', count: 1820, momPct: 3 },
  { name: 'Oregon', type: 'state', country: 'US', count: 1612, momPct: 7 },
  { name: 'Virginia', type: 'state', country: 'US', count: 1402, momPct: 5 },
  { name: 'Georgia', type: 'state', country: 'US', count: 1289, momPct: 8 },
  { name: 'South Carolina', type: 'state', country: 'US', count: 412, momPct: 14 },
  { name: 'Washington', type: 'state', country: 'US', count: 389, momPct: 2 },
];

const FALLBACK_CA_PROVINCES: Territory[] = [
  { name: 'Ontario', type: 'province', country: 'CA', count: 2520, momPct: 12 },
  { name: 'British Columbia', type: 'province', country: 'CA', count: 890, momPct: 9 },
  { name: 'Alberta', type: 'province', country: 'CA', count: 520, momPct: 8 },
  { name: 'Quebec', type: 'province', country: 'CA', count: 380, momPct: 6 },
  { name: 'Manitoba', type: 'province', country: 'CA', count: 120, momPct: 4 },
  { name: 'Nova Scotia', type: 'province', country: 'CA', count: 90, momPct: 7 },
  { name: 'Saskatchewan', type: 'province', country: 'CA', count: 65, momPct: 5 },
];

const FALLBACK_POPULAR: CityResult[] = [
  { name: 'Berkeley', state: 'CA', permits: 142 },
  { name: 'Palo Alto', state: 'CA', permits: 98 },
  { name: 'Fremont', state: 'CA', permits: 76 },
  { name: 'Hayward', state: 'CA', permits: 54 },
];

// ── Helpers ──
function deltaClass(pct: number): 'hot' | 'up' | 'flat' | 'down' {
  if (pct < 0) return 'down';
  if (pct > 15) return 'hot';
  if (pct < 2) return 'flat';
  return 'up';
}
function deltaLabel(pct: number): string {
  if (pct < 0) return `▼ ${pct}%`;
  if (pct < 2 && pct >= 0) return `+${pct}%`;
  return `▲ +${pct}%`;
}
function deltaColor(pct: number): string {
  const c = deltaClass(pct);
  if (c === 'hot') return T.primary;
  if (c === 'up') return T.grow;
  if (c === 'down') return T.defend;
  return '#0A0A0A';
}
function fmtNum(n: number): string { return n.toLocaleString('en-US'); }

// Coverage tier: the rep's first question is "is this area worth working?"
// Volume, not momentum. Deep = build a pipeline here; Thin = barely any doors.
function coverageTier(n: number): { label: string; color: string } {
  if ((n || 0) >= 2000) return { label: 'Deep', color: '#16A34A' };
  if ((n || 0) >= 200)  return { label: 'Workable', color: '#0A0A0A' };
  return { label: 'Thin', color: '#B45309' };
}
function lc(s: string): string { return (s || '').toLowerCase(); }

// ══════════════════════════════════════════════════════════════════════════════
export default function TerritoryPicker({
  isOpen, onClose, onSelect, currentTerritory, apiBase, anchorPos,
}: Props) {
  const base = apiBase || "https://solarlead-backend-production.up.railway.app";

  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  const [mode, setMode] = useState<'browse' | 'build'>('browse');
  const [searchQuery, setSearchQuery] = useState('');
  const [usStates, setUsStates] = useState<Territory[]>(FALLBACK_US_STATES);
  const [caProvinces, setCaProvinces] = useState<Territory[]>(FALLBACK_CA_PROVINCES);
  const [recent, setRecent] = useState<Territory[]>([]);
  const [saved, setSaved] = useState<Territory[]>([]);

  // Build mode state
  const [buildName, setBuildName] = useState('');
  const [buildCities, setBuildCities] = useState<CityResult[]>([]);
  const [citySearch, setCitySearch] = useState('');
  const [citySearchResults, setCitySearchResults] = useState<CityResult[]>([]);
  const [popularNearby, setPopularNearby] = useState<CityResult[]>(FALLBACK_POPULAR);

  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // ── Load on open ──
  useEffect(() => {
    if (!isOpen) return;
    setMode('browse');
    setSearchQuery('');
    setBuildName('');
    setBuildCities([]);
    setCitySearch('');
    setCitySearchResults([]);

    try {
      const r = localStorage.getItem('sl_recent_territories');
      if (r) {
        const parsed = JSON.parse(r);
        if (Array.isArray(parsed)) setRecent(parsed.slice(0, 3));
      }
    } catch {}
    try {
      const s = localStorage.getItem('sl_saved_territories');
      if (s) {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed)) setSaved(parsed);
      }
    } catch {}

    fetch(`${base}/api/territories/list`)
      .then(r => r.json())
      .then(data => {
        if (data?.usStates && Array.isArray(data.usStates)) setUsStates(data.usStates);
        if (data?.caProvinces && Array.isArray(data.caProvinces)) setCaProvinces(data.caProvinces);
        // Legacy fallback: older /api/territories/list returns { states: [...] }
        if (data?.states && Array.isArray(data.states) && !data.usStates) setUsStates(data.states);
      })
      .catch(() => {});

    setTimeout(() => searchInputRef.current?.focus(), 50);
  }, [isOpen, base]);

  // ── Esc closes ──
  useEffect(() => {
    if (!isOpen) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [isOpen, onClose]);

  // ── City search (debounced) ──
  useEffect(() => {
    if (mode !== 'build') return;
    if (!citySearch.trim()) { setCitySearchResults([]); return; }
    const q = citySearch.trim();
    const id = setTimeout(() => {
      fetch(`${base}/api/cities/search?q=${encodeURIComponent(q)}`)
        .then(r => r.json())
        .then(data => {
          if (data?.results && Array.isArray(data.results)) setCitySearchResults(data.results.slice(0, 6));
        })
        .catch(() => setCitySearchResults([]));
    }, 220);
    return () => clearTimeout(id);
  }, [citySearch, mode, base]);

  // ── Popular nearby (refresh as cities added) ──
  useEffect(() => {
    if (mode !== 'build') return;
    if (buildCities.length === 0) { setPopularNearby(FALLBACK_POPULAR); return; }
    const cityNames = buildCities.map(c => c.name).join(',');
    fetch(`${base}/api/cities/nearby?cities=${encodeURIComponent(cityNames)}`)
      .then(r => r.json())
      .then(data => {
        if (data?.results && Array.isArray(data.results)) {
          const addedSet = new Set(buildCities.map(c => lc(c.name)));
          setPopularNearby(data.results.filter((c: CityResult) => !addedSet.has(lc(c.name))).slice(0, 4));
        }
      })
      .catch(() => {});
  }, [buildCities, mode, base]);

  // ── Filtered views ──
  const filteredUsStates = useMemo(() => {
    const q = lc(searchQuery);
    const base = !searchQuery.trim() ? usStates : usStates.filter(s => lc(s.name).includes(q));
    return base.slice().sort((a, b) => (b.count || 0) - (a.count || 0));
  }, [usStates, searchQuery]);

  const filteredCaProvinces = useMemo(() => {
    const q = lc(searchQuery);
    const base = !searchQuery.trim() ? caProvinces : caProvinces.filter(p => lc(p.name).includes(q));
    return base.slice().sort((a, b) => (b.count || 0) - (a.count || 0));
  }, [caProvinces, searchQuery]);

  const filteredSaved = useMemo(() => {
    if (!searchQuery.trim()) return saved;
    const q = lc(searchQuery);
    return saved.filter(s => lc(s.name).includes(q));
  }, [saved, searchQuery]);

  const filteredRecent = useMemo(() => {
    if (!searchQuery.trim()) return recent;
    const q = lc(searchQuery);
    return recent.filter(r => lc(r.name).includes(q));
  }, [recent, searchQuery]);

  const showAllMarkets = !searchQuery.trim() || lc('All Markets').includes(lc(searchQuery));

  const allMarketsCount = useMemo(() => {
    const sum = [...usStates, ...caProvinces].reduce((acc, t) => acc + (t.count || 0), 0);
    return sum > 0 ? sum : ALL_MARKETS.count;
  }, [usStates, caProvinces]);

  // ── Handlers ──
  const selectTerritory = (t: Territory) => {
    // Skip persisting "All Markets" to Recent — it's always available as default
    if (t.name !== currentTerritory && t.type !== 'all') {
      const existingRaw = (() => { try { return JSON.parse(localStorage.getItem('sl_recent_territories') || '[]'); } catch { return []; } })();
      const filtered = Array.isArray(existingRaw) ? existingRaw.filter((x: Territory) => x.name !== t.name) : [];
      const next = [{ name: t.name, type: t.type, country: t.country, count: t.count, momPct: t.momPct, cities: t.cities, cityList: t.cityList }, ...filtered].slice(0, 3);
      try { localStorage.setItem('sl_recent_territories', JSON.stringify(next)); } catch {}
    }
    onSelect(t.name);
    onClose();
  };

  const addCity = (c: CityResult) => {
    if (buildCities.some(x => lc(x.name) === lc(c.name))) return;
    setBuildCities([...buildCities, c]);
    setCitySearch('');
    setCitySearchResults([]);
  };

  const removeCity = (name: string) => {
    setBuildCities(buildCities.filter(c => lc(c.name) !== lc(name)));
  };

  const saveCustomTerritory = () => {
    if (!buildName.trim() || buildCities.length === 0) return;
    const totalPermits = buildCities.reduce((sum, c) => sum + (c.permits || 0), 0);
    const newTerritory: Territory = {
      name: buildName.trim(),
      type: 'custom',
      count: totalPermits,
      momPct: 0,
      cities: buildCities.length,
      cityList: buildCities.map(c => c.name),
    };
    const nextSaved = [newTerritory, ...saved.filter(s => s.name !== newTerritory.name)];
    try { localStorage.setItem('sl_saved_territories', JSON.stringify(nextSaved)); } catch {}
    setSaved(nextSaved);
    selectTerritory(newTerritory);
  };

  if (!isOpen) return null;

  // ── Positioning — anchored to button if anchorPos given, else fallback ──
  const VIEWPORT_W = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const PANEL_W = 460;
  const anchorTop = anchorPos?.top ?? 56;
  const anchorLeft = anchorPos?.left ?? 28;
  // Clamp to viewport — don't let panel run off right edge
  const safeLeft = Math.min(anchorLeft, VIEWPORT_W - PANEL_W - 16);

  const panelStyle: React.CSSProperties = isMobile ? {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    zIndex: 1001,
    background: T.surface,
    display: 'flex', flexDirection: 'column',
  } : {
    position: 'fixed',
    top: anchorTop,
    left: Math.max(16, safeLeft),
    zIndex: 1001,
    width: PANEL_W,
    maxHeight: `calc(100vh - ${anchorTop + 32}px)`,
    background: T.surface,
    border: `1px solid ${T.borderDark}`,
    boxShadow: '0 12px 32px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.04)',
    display: 'flex', flexDirection: 'column',
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: isMobile ? 'transparent' : 'rgba(0,0,0,0.02)',
        }}
      />

      <div style={panelStyle}>
        {mode === 'browse' ? (
          <>
            <PickerHeader title="Switch territory" onClose={onClose} />

            <div style={{ padding: '12px 18px', borderBottom: `1px solid ${T.borderHairline}` }}>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search states, provinces, cities..."
                style={{
                  width: '100%', background: T.surfaceRaised, border: `1px solid ${T.border}`,
                  padding: '9px 12px', fontFamily: "'Inter', sans-serif", fontSize: 13,
                  color: T.text, outline: 'none', boxSizing: 'border-box',
                }}
                onFocus={e => { e.currentTarget.style.borderColor = T.text; e.currentTarget.style.background = T.surface; }}
                onBlur={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = T.surfaceRaised; }}
              />
            </div>

            <div style={{ overflowY: 'auto', flex: 1 }}>

              {/* Current */}
              {!searchQuery && (
                <Section title="Current territory">
                  <CurrentRow name={currentTerritory} />
                </Section>
              )}

              {/* All Markets — always near top so default is one click away */}
              {showAllMarkets && (
                <Section title="All markets">
                  <TerritoryRow
                    territory={{ ...ALL_MARKETS, count: allMarketsCount }}
                    onClick={() => selectTerritory(ALL_MARKETS)}
                    subtitle="US + Canada combined"
                  />
                </Section>
              )}

              {/* Recent */}
              {filteredRecent.length > 0 && (
                <Section title="Recent">
                  {filteredRecent.map((t, i) => (
                    <TerritoryRow key={`recent-${i}`} territory={t} onClick={() => selectTerritory(t)} />
                  ))}
                </Section>
              )}

              {/* Saved */}
              <Section
                title="Saved territories"
                rightAction={{ label: '+ New', onClick: () => setMode('build') }}
              >
                {filteredSaved.length === 0 && !searchQuery && (
                  <div style={{ padding: '4px 18px 8px', fontSize: 12, color: T.textSub, fontWeight: 500, lineHeight: 1.5, fontStyle: 'italic' }}>
                    Build a custom bundle to combine cities into one territory.
                  </div>
                )}
                {filteredSaved.map((t, i) => (
                  <TerritoryRow key={`saved-${i}`} territory={t} onClick={() => selectTerritory(t)} />
                ))}
              </Section>

              {/* US states */}
              {filteredUsStates.length > 0 && (
                <Section title="United States" rightLabel="VOLUME · MoM">
                  <div style={{ maxHeight: isMobile ? 'none' : 220, overflowY: 'auto' }}>
                    {filteredUsStates.map((t, i) => (
                      <TerritoryRow key={`us-${i}`} territory={t} onClick={() => selectTerritory(t)} compact />
                    ))}
                  </div>
                </Section>
              )}

              {/* Canada provinces */}
              {filteredCaProvinces.length > 0 && (
                <Section title="Canada" rightLabel="VOLUME · MoM">
                  <div style={{ maxHeight: isMobile ? 'none' : 180, overflowY: 'auto' }}>
                    {filteredCaProvinces.map((t, i) => (
                      <TerritoryRow key={`ca-${i}`} territory={t} onClick={() => selectTerritory(t)} compact />
                    ))}
                  </div>
                </Section>
              )}

              {/* Empty search */}
              {searchQuery && filteredUsStates.length === 0 && filteredCaProvinces.length === 0 && filteredRecent.length === 0 && filteredSaved.length === 0 && !showAllMarkets && (
                <div style={{ padding: '16px 18px', fontSize: 12, color: T.textSub, fontWeight: 500, fontStyle: 'italic' }}>
                  No territories match "{searchQuery}".
                </div>
              )}
            </div>
          </>
        ) : (
          // ════════════ BUILD MODE (unchanged from v1) ════════════
          <>
            <PickerHeader
              title="Build territory"
              onBack={() => setMode('browse')}
              onClose={onClose}
            />

            <div style={{ overflowY: 'auto', flex: 1 }}>
              <div style={{ padding: '14px 18px', borderBottom: `1px solid ${T.borderHairline}` }}>
                <BuilderLabel>Name your territory</BuilderLabel>
                <input
                  type="text"
                  value={buildName}
                  onChange={e => setBuildName(e.target.value)}
                  placeholder="e.g. Bay Area, Inland Empire"
                  autoFocus
                  style={{
                    width: '100%', background: T.surfaceRaised, border: `1px solid ${T.border}`,
                    padding: '10px 12px', fontFamily: FONT_DISPLAY, fontSize: 16, fontWeight: 800,
                    color: T.text, outline: 'none', letterSpacing: '-0.01em', boxSizing: 'border-box',
                  }}
                  onFocus={e => { e.currentTarget.style.borderColor = T.text; e.currentTarget.style.background = T.surface; }}
                  onBlur={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = T.surfaceRaised; }}
                />
              </div>

              <div style={{ padding: '14px 18px', borderBottom: `1px solid ${T.borderHairline}` }}>
                <BuilderLabel>Add cities</BuilderLabel>
                <input
                  type="text"
                  value={citySearch}
                  onChange={e => setCitySearch(e.target.value)}
                  placeholder="Search by city name or zip..."
                  style={{
                    width: '100%', background: T.surfaceRaised, border: `1px solid ${T.border}`,
                    padding: '10px 12px', fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 500,
                    color: T.text, outline: 'none', boxSizing: 'border-box',
                  }}
                  onFocus={e => { e.currentTarget.style.borderColor = T.text; e.currentTarget.style.background = T.surface; }}
                  onBlur={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = T.surfaceRaised; }}
                />

                {citySearchResults.length > 0 && (
                  <div style={{ marginTop: 8, border: `1px solid ${T.borderHairline}`, background: T.surface }}>
                    {citySearchResults.map((c, i) => (
                      <div
                        key={i}
                        onClick={() => addCity(c)}
                        style={{
                          padding: '8px 12px', cursor: 'pointer',
                          borderBottom: i < citySearchResults.length - 1 ? `1px solid ${T.borderHairline}` : 'none',
                          display: 'flex', alignItems: 'center', gap: 10,
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = T.surfaceRaised)}
                        onMouseLeave={e => (e.currentTarget.style.background = T.surface)}
                      >
                        <span style={{ fontSize: 14, color: T.textSub, fontWeight: 700 }}>+</span>
                        <span style={{ flex: 1, fontFamily: FONT_DISPLAY, fontSize: 13, fontWeight: 700, color: T.text }}>
                          {c.name}, {c.state}
                        </span>
                        <span style={{ fontFamily: FONT_MONO, fontSize: 10.5, fontWeight: 700, display: 'inline-flex', gap: 7, alignItems: 'baseline' }}>
                          <span style={{ color: T.text }}>{fmtNum(c.permits)}</span>
                          <span style={{ color: coverageTier(c.permits).color, fontWeight: 800, fontSize: 9, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{coverageTier(c.permits).label}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {buildCities.length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <BuilderLabel style={{ marginBottom: 8 }}>Added · {buildCities.length}</BuilderLabel>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {buildCities.map((c, i) => (
                        <span
                          key={i}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            background: T.surfaceMagic, border: '1px solid rgba(255,136,0,0.25)',
                            padding: '4px 8px 4px 10px',
                            fontFamily: FONT_DISPLAY, fontSize: 12, fontWeight: 800, color: T.text,
                            letterSpacing: '-0.005em',
                          }}
                        >
                          {c.name}, {c.state}
                          <span
                            onClick={() => removeCity(c.name)}
                            style={{ cursor: 'pointer', fontSize: 14, color: T.textSub, fontWeight: 700, marginLeft: 2, lineHeight: 1 }}
                            onMouseEnter={e => (e.currentTarget.style.color = T.defend)}
                            onMouseLeave={e => (e.currentTarget.style.color = T.textSub)}
                          >×</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {popularNearby.length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <BuilderLabel style={{ marginBottom: 4 }}>Popular nearby</BuilderLabel>
                    <div style={{ marginTop: 6 }}>
                      {popularNearby.map((c, i) => (
                        <div
                          key={i}
                          onClick={() => addCity(c)}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', cursor: 'pointer' }}
                        >
                          <span
                            style={{
                              width: 18, height: 18, border: `1.5px solid ${T.textSub}`,
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              color: T.textSub, fontSize: 14, fontWeight: 700, lineHeight: 1,
                            }}
                          >+</span>
                          <span style={{ fontFamily: FONT_DISPLAY, fontSize: 13, fontWeight: 700, color: T.text }}>
                            {c.name}, {c.state}
                          </span>
                          <span style={{ marginLeft: 'auto', fontFamily: FONT_MONO, fontSize: 10.5, fontWeight: 700, display: 'inline-flex', gap: 7, alignItems: 'baseline' }}>
                            <span style={{ color: T.text }}>{fmtNum(c.permits)}</span>
                            <span style={{ color: coverageTier(c.permits).color, fontWeight: 800, fontSize: 9, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{coverageTier(c.permits).label}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {buildCities.length > 0 && (
                <div style={{
                  background: T.surfaceRaised, padding: '12px 18px',
                  borderTop: `1px solid ${T.borderHairline}`,
                  fontFamily: FONT_MONO, fontSize: 10.5, color: T.text, fontWeight: 700,
                  letterSpacing: '0.04em', textTransform: 'uppercase',
                }}>
                  Your territory will cover
                  <div style={{
                    fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 900, color: T.text,
                    letterSpacing: '-0.025em', textTransform: 'none', marginTop: 2,
                  }}>
                    {buildCities.length} {buildCities.length === 1 ? 'city' : 'cities'} ·{' '}
                    <span style={{ color: T.primary }}>
                      {fmtNum(buildCities.reduce((sum, c) => sum + (c.permits || 0), 0))} active permits
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div style={{ padding: '12px 18px', borderTop: `1px solid ${T.borderHairline}`, display: 'flex', gap: 8 }}>
              <button
                onClick={() => setMode('browse')}
                style={{
                  flex: 1, padding: '10px 14px', border: `1.5px solid ${T.text}`, background: T.surface, color: T.text,
                  fontFamily: FONT_DISPLAY, fontSize: 12.5, fontWeight: 900, letterSpacing: '-0.005em',
                  cursor: 'pointer',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = T.text; e.currentTarget.style.color = '#FFFFFF'; }}
                onMouseLeave={e => { e.currentTarget.style.background = T.surface; e.currentTarget.style.color = T.text; }}
              >
                Cancel
              </button>
              <button
                onClick={saveCustomTerritory}
                disabled={!buildName.trim() || buildCities.length === 0}
                style={{
                  flex: 1, padding: '10px 14px', border: 'none',
                  background: (!buildName.trim() || buildCities.length === 0) ? T.border : T.primary,
                  color: (!buildName.trim() || buildCities.length === 0) ? '#999992' : '#FFFFFF',
                  fontFamily: FONT_DISPLAY, fontSize: 12.5, fontWeight: 900, letterSpacing: '-0.005em',
                  cursor: (!buildName.trim() || buildCities.length === 0) ? 'not-allowed' : 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
                onMouseEnter={e => { if (buildName.trim() && buildCities.length > 0) e.currentTarget.style.background = T.primaryDark; }}
                onMouseLeave={e => { if (buildName.trim() && buildCities.length > 0) e.currentTarget.style.background = T.primary; }}
              >
                Save territory →
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────────────

function PickerHeader({ title, onClose, onBack }: { title: string; onClose: () => void; onBack?: () => void }) {
  return (
    <div style={{
      padding: '14px 18px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      borderBottom: `1px solid ${T.border}`,
    }}>
      {onBack ? (
        <div
          onClick={onBack}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
            fontFamily: FONT_DISPLAY, fontSize: 12, fontWeight: 800, color: T.textSub,
          }}
          onMouseEnter={e => (e.currentTarget.style.color = T.text)}
          onMouseLeave={e => (e.currentTarget.style.color = T.textSub)}
        >
          ← Back to picker
        </div>
      ) : (
        <div style={{ fontFamily: FONT_DISPLAY, fontSize: 14, fontWeight: 900, color: T.text, letterSpacing: '-0.01em' }}>
          {title}
        </div>
      )}
      {onBack && (
        <div style={{
          fontFamily: FONT_MONO, fontSize: 11, letterSpacing: '0.2em', color: T.textSub, fontWeight: 700, textTransform: 'uppercase',
        }}>
          {title}
        </div>
      )}
      {!onBack && (
        <div
          onClick={onClose}
          style={{
            width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: T.textSub, fontSize: 18, lineHeight: 1,
          }}
          onMouseEnter={e => { e.currentTarget.style.color = T.text; e.currentTarget.style.background = T.surfaceRaised; }}
          onMouseLeave={e => { e.currentTarget.style.color = T.textSub; e.currentTarget.style.background = 'transparent'; }}
        >×</div>
      )}
    </div>
  );
}

function Section({
  title, children, rightAction, rightLabel,
}: {
  title: string;
  children: React.ReactNode;
  rightAction?: { label: string; onClick: () => void };
  rightLabel?: string;
}) {
  return (
    <div style={{ padding: '12px 0', borderBottom: `1px solid ${T.borderHairline}` }}>
      <div style={{
        padding: '4px 18px 8px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: '0.2em',
        color: T.textSub, fontWeight: 700, textTransform: 'uppercase',
      }}>
        <span>{title}</span>
        {rightAction && (
          <span
            onClick={rightAction.onClick}
            style={{ color: T.primary, cursor: 'pointer', fontWeight: 700 }}
            onMouseEnter={e => (e.currentTarget.style.color = T.primaryDark)}
            onMouseLeave={e => (e.currentTarget.style.color = T.primary)}
          >{rightAction.label}</span>
        )}
        {rightLabel && (
          <span style={{ color: T.text, fontWeight: 700 }}>{rightLabel}</span>
        )}
      </div>
      {children}
    </div>
  );
}

function CurrentRow({ name }: { name: string }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '10px 18px',
      background: T.surfaceMagic, borderLeft: `3px solid ${T.primary}`,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ display: 'inline-block', width: 7, height: 7, background: T.primary }} />
          <span style={{
            fontFamily: FONT_DISPLAY, fontSize: 14, fontWeight: 800, color: T.text, letterSpacing: '-0.01em',
          }}>{name}</span>
        </div>
        <div style={{
          fontFamily: FONT_MONO, fontSize: 10, color: T.textSub, fontWeight: 500,
          marginTop: 4, letterSpacing: '0.04em', textTransform: 'uppercase',
        }}>
          Active territory · pulling fresh permits
        </div>
      </div>
    </div>
  );
}

function TerritoryRow({
  territory, onClick, compact, subtitle,
}: {
  territory: Territory; onClick: () => void; compact?: boolean; subtitle?: string;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: compact ? '7px 18px' : '10px 18px',
        cursor: 'pointer',
        background: hover ? T.surfaceRaised : 'transparent',
        borderLeft: `3px solid ${hover ? T.borderHairline : 'transparent'}`,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: FONT_DISPLAY, fontSize: compact ? 13 : 14,
          fontWeight: compact ? 700 : 800, color: T.text, letterSpacing: '-0.01em',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{territory.name}</div>
        {subtitle && (
          <div style={{
            fontFamily: FONT_MONO, fontSize: 10, color: T.textSub, fontWeight: 500,
            marginTop: 3, letterSpacing: '0.04em', textTransform: 'uppercase',
          }}>{subtitle}</div>
        )}
      </div>

      {territory.type === 'custom' && territory.cities && (
        <span style={{
          fontFamily: FONT_MONO, fontSize: 10, color: T.textSub, fontWeight: 500, letterSpacing: '0.04em',
        }}>{territory.cities} cities</span>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 64 }}>
        <span style={{
          fontFamily: FONT_MONO, fontSize: 11, color: T.text, fontWeight: 700, letterSpacing: '0.04em',
        }}>{fmtNum(territory.count)}</span>
        {territory.type !== 'all' && (() => {
          const tr = coverageTier(territory.count);
          return (
            <span style={{
              fontFamily: FONT_MONO, fontSize: 8.5, fontWeight: 800, letterSpacing: '0.06em',
              textTransform: 'uppercase', color: tr.color, marginTop: 2,
            }}>{tr.label}</span>
          );
        })()}
      </div>

      <span style={{
        fontFamily: FONT_MONO, fontSize: 10, fontWeight: 700,
        minWidth: 56, textAlign: 'right', letterSpacing: '0.03em',
        color: deltaColor(territory.momPct),
      }}>{deltaLabel(territory.momPct)}</span>
    </div>
  );
}

function BuilderLabel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{
      fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: '0.2em',
      color: T.textSub, fontWeight: 700, textTransform: 'uppercase',
      marginBottom: 8, ...style,
    }}>{children}</div>
  );
}
